// TODO — swap in react-i18next once it's installed in the consuming app.
// Today this is a minimal in-memory translation store with the same API
// surface so countries can write `t('billing.invoices.title')` now.

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { Locale } from '@fluxora/types';

export type TranslationDict = Record<string, string>;
export type Namespaces = Record<string, TranslationDict>;
export type Resources = Record<Locale, Namespaces>;

export interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  addResources: (locale: Locale, namespace: string, dict: TranslationDict) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/** Same keys, identical string values — the cheap content check the sync bails on. */
function shallowEqualDict(a: TranslationDict, b: TranslationDict): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, k: string) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{{${k}}}`,
  );
}

export interface I18nProviderProps {
  children: ReactNode;
  locale: Locale;
  resources?: Resources;
}

export function I18nProvider({ children, locale: initial, resources = {} as Resources }: I18nProviderProps) {
  const [locale, setLocale] = useState<Locale>(initial);
  const [store, setStore] = useState<Resources>(resources);

  // Keep following the `resources` prop after mount. The store used to be
  // captured ONCE (useState initial) and never looked at the prop again, which
  // froze every translation for the tab's lifetime: under Vite HMR an edited
  // locale module re-rendered the provider with fresh resources that were then
  // silently ignored, so new keys rendered as raw `ns.key` strings until a
  // full browser reload. Merge, don't replace: namespaces added at runtime via
  // addResources() survive — the prop only overwrites namespaces it carries.
  //
  // ⚠️ THE BAIL-OUT IS LOAD-BEARING. `resources` is often a FRESH object every
  // render (the default `{}`, or an inline literal at the call site), so this
  // effect can fire per render. Returning the PREVIOUS state object when no
  // dictionary actually changed makes React skip the re-render — without that,
  // setStore(new object) → re-render → new prop identity → effect →
  // "Maximum update depth exceeded", which took the whole CRM down.
  useEffect(() => {
    setStore((prev) => {
      let changed = false;
      const next: Resources = { ...prev };
      for (const loc of Object.keys(resources) as Locale[]) {
        const incoming = resources[loc];
        if (incoming === undefined) continue;
        for (const ns of Object.keys(incoming)) {
          const dict = incoming[ns];
          if (dict === undefined) continue;
          const current = next[loc]?.[ns];
          if (current !== undefined && shallowEqualDict(current, dict)) continue;
          next[loc] = { ...(next[loc] ?? {}), [ns]: dict };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [resources]);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const [ns, ...rest] = key.split('.');
      const dotKey = rest.join('.');
      const dict = ns ? store[locale]?.[ns] : undefined;
      const value = dict?.[dotKey] ?? store[locale]?.['common']?.[key] ?? key;
      return interpolate(value, vars);
    },
    [locale, store],
  );

  const addResources = useCallback(
    (loc: Locale, namespace: string, dict: TranslationDict) => {
      setStore((prev) => ({
        ...prev,
        [loc]: { ...(prev[loc] ?? {}), [namespace]: { ...(prev[loc]?.[namespace] ?? {}), ...dict } },
      }));
    },
    [],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t, addResources }),
    [locale, t, addResources],
  );

  return createElement(I18nContext.Provider, { value }, children);
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used within <I18nProvider>');
  return ctx;
}
